import { classes, events } from '@theme/constants';
import { on } from '@theme/dom';
import { publish } from '@theme/event-bus';
import { fetchConfig, json } from '@theme/fetch';
import { getCartSectionNames } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class LineItem extends HTMLCustomElement {
  requiredAttributes = ['key'];

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('change', this.refs.quantity, this.handleQuantityChange.bind(this));
    this.on('click', this.refs.remove, this.handleRemoveEvent.bind(this));
    this.on('change', this.refs.sellingPlan, this.handleSellingPlanEvent.bind(this));
    this.on('click', this.refs.applyDefaultSellingPlan, this.applyDefaultSellingPlan.bind(this));
  }

  /**
   * Handles the selling plan change event.
   * @param {InputEvent} event - The selling plan input change event.
   */
  handleSellingPlanEvent(event) {
    if (this.refs.sellingPlan) {
      this.updateItem({ selling_plan: event.target.value });
    }
  }

  /**
   * Applies the default selling plan.
   */
  applyDefaultSellingPlan() {
    if (this.hasAttribute('default-selling-plan')) {
      this.updateItem({ selling_plan: this.getAttribute('default-selling-plan') });
    }
  }

  /**
   * Handles the quantity change event.
   * @param {InputEvent} event - The quantity input change event.
   */
  handleQuantityChange(event) {
    this.updateItem({ quantity: Number(event.target.value) });
  }

  /**
   * Handles the remove click event.
   * @param {MouseEvent} event - The remove click event.
   */
  handleRemoveEvent(event) {
    event.preventDefault();

    /**
     * Set the item's quantity to 0 to remove it.
     */
    this.updateItem({ quantity: 0 });
  }

  /**
   * Updates the line item in the cart.
   * @param {object} updates - The update data.
   * @param {number} updates.quantity - The line item quantity.
   * @param {string} updates.selling_plan - The selling plan.
   */
  async updateItem({ quantity, selling_plan } = {}) {
    /**
     * Prevents the event.
     * @param {MouseEvent} event - The event payload.
     */
    function prevent(event) {
      event.preventDefault();
    }

    this.classList.add(classes.loading);

    /**
     * Stops the customer from triggering any actions whilst loading.
     */
    this.removeMouseDownEvent = on('mousedown', this, prevent);

    try {
      await json(
        fetch(
          `${theme.routes.cart_change_url}.js`,
          fetchConfig('json', {
            body: JSON.stringify({
              id: this.getAttribute('key'),
              ...(typeof quantity === 'undefined' ? null : { quantity }),
              ...(typeof selling_plan === 'undefined' ? null : { selling_plan }),
              sections: getCartSectionNames().join(','),
            }),
          }),
        ),
      ).then((response) => {
        this.reset();
        this.removeMouseDownEvent();

        if (response.status) {
          throw new Error(response.message);
        }

        publish(events.cartUpdated);
        publish(events.sectionUpdate, { sections: response.sections });
      });
    } catch {
      this.reset();
    }
  }

  /**
   * Resets the line item state.
   */
  reset() {
    this.classList.remove(classes.loading);
  }
}

if (!customElements.get('line-item')) customElements.define('line-item', LineItem);
